import React, { useState } from 'react';
import { useNavigate, useLocation, Navigate, Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useAuth } from '../auth/AuthContext.jsx';
import { ErrorBanner, FieldError } from '../components/ui/index.ts';
import { useServerErrorHandler } from '../hooks/useServerErrorHandler.ts';
import { useSubmitState } from '../hooks/useSubmitState.ts';
import '../styles/pages/auth.css';

const registerSchema = z
  .object({
    email: z.string().min(1, 'Email is required').email('Enter a valid email'),
    title: z.string().optional().default(''),
    password: z
      .string()
      .min(1, 'Password is required')
      .min(8, 'Password must be at least 8 characters'),
    confirmPassword: z.string().min(1, 'Please confirm your password'),
  })
  .refine((data) => data.password === data.confirmPassword, {
    path: ['confirmPassword'],
    message: 'Passwords do not match.',
  });

const confirmSchema = z.object({
  code: z
    .string()
    .min(1, 'Verification code is required')
    .regex(/^\d+$/, 'Code must contain only digits'),
});

const REGISTER_RESOLVER = zodResolver(registerSchema);
const CONFIRM_RESOLVER = zodResolver(confirmSchema);

export default function RegisterPage() {
  const { register: registerUser, confirmSignUp, isAuthenticated, isLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // unified-auth Phase 4: the email-first LOCAL/signup fall-through on
  // /login hands off here with the typed email prefilled via router state
  // (never a query param, to avoid leaking it into logs/history).
  const prefilledEmail =
    typeof location.state?.email === 'string' ? location.state.email : '';

  // All hooks MUST be called before any conditional returns (React rules of hooks)
  const [step, setStep] = useState('register'); // 'register' | 'confirm'
  const [submittedEmail, setSubmittedEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isConfirmSubmitting, setIsConfirmSubmitting] = useState(false);

  const registerForm = useForm({
    resolver: REGISTER_RESOLVER,
    defaultValues: { email: prefilledEmail, title: '', password: '', confirmPassword: '' },
  });
  const {
    register: registerField,
    handleSubmit: handleRegisterSubmit,
    formState: { errors: registerErrors },
  } = registerForm;

  const confirmForm = useForm({
    resolver: CONFIRM_RESOLVER,
    defaultValues: { code: '' },
  });
  const {
    register: confirmField,
    handleSubmit: handleConfirmSubmit,
    formState: { errors: confirmErrors },
  } = confirmForm;

  const {
    bannerMessage: registerBanner,
    handleServerError: handleRegisterServerError,
    clearBanner: clearRegisterBanner,
  } = useServerErrorHandler(registerForm);

  const {
    bannerMessage: confirmBanner,
    handleServerError: handleConfirmServerError,
    clearBanner: clearConfirmBanner,
  } = useServerErrorHandler(confirmForm);

  const submitState = useSubmitState({
    isSubmitting,
    baseLabel: 'Create account',
    loadingLabel: 'Creating account…',
  });

  const confirmSubmitState = useSubmitState({
    isSubmitting: isConfirmSubmitting,
    baseLabel: 'Verify email',
    loadingLabel: 'Verifying…',
  });

  // Redirect to main app if already signed in
  if (!isLoading && isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  async function onRegister(values) {
    clearRegisterBanner();
    setIsSubmitting(true);
    try {
      await registerUser(values.email, values.password, values.title ?? '');
      setSubmittedEmail(values.email);
      setStep('confirm');
    } catch (err) {
      handleRegisterServerError(err);
    } finally {
      setIsSubmitting(false);
    }
  }

  async function onConfirm(values) {
    clearConfirmBanner();
    setIsConfirmSubmitting(true);
    try {
      await confirmSignUp(submittedEmail, values.code);
      navigate('/login');
    } catch (err) {
      handleConfirmServerError(err);
    } finally {
      setIsConfirmSubmitting(false);
    }
  }

  if (step === 'confirm') {
    return (
      <div className="auth-page">
        <div className="auth-card">
          <h1 className="auth-title">Verify your email</h1>
          <p className="auth-subtitle">
            We sent a verification code to <strong>{submittedEmail}</strong>. Enter it
            below to complete registration.
          </p>

          <form
            onSubmit={handleConfirmSubmit(onConfirm)}
            data-testid="register-confirm-form"
            noValidate
          >
            {confirmBanner && (
              <ErrorBanner message={confirmBanner} onDismiss={clearConfirmBanner} />
            )}

            <div className="form-field">
              <label htmlFor="code">Verification code</label>
              <input
                id="code"
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                disabled={isConfirmSubmitting}
                aria-invalid={!!confirmErrors.code || undefined}
                aria-describedby={confirmErrors.code ? 'code-error' : undefined}
                {...confirmField('code')}
              />
              <FieldError id="code-error" error={confirmErrors.code} />
            </div>

            <button
              type="submit"
              data-testid="submit-btn"
              className="btn-primary btn-full"
              aria-busy={confirmSubmitState.ariaBusy}
              disabled={confirmSubmitState.ariaBusy}
            >
              {confirmSubmitState.buttonLabel}
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-page">
      <div className="auth-card">
        <h1 className="auth-title">Create your account</h1>

        <form
          onSubmit={handleRegisterSubmit(onRegister)}
          data-testid="register-form"
          noValidate
        >
          {registerBanner && (
            <ErrorBanner message={registerBanner} onDismiss={clearRegisterBanner} />
          )}

          <div className="form-field">
            <label htmlFor="email">Email</label>
            <input
              id="email"
              type="email"
              autoComplete="email"
              disabled={isSubmitting}
              aria-invalid={!!registerErrors.email || undefined}
              aria-describedby={registerErrors.email ? 'email-error' : undefined}
              {...registerField('email')}
            />
            <FieldError id="email-error" error={registerErrors.email} />
          </div>

          <div className="form-field">
            <label htmlFor="title">Title / Role</label>
            <input
              id="title"
              type="text"
              autoComplete="organization-title"
              placeholder="e.g. VP of Operations"
              disabled={isSubmitting}
              aria-invalid={!!registerErrors.title || undefined}
              aria-describedby={registerErrors.title ? 'title-error' : undefined}
              {...registerField('title')}
            />
            <FieldError id="title-error" error={registerErrors.title} />
          </div>

          <div className="form-field">
            <label htmlFor="password">Password</label>
            <input
              id="password"
              type="password"
              autoComplete="new-password"
              disabled={isSubmitting}
              aria-invalid={!!registerErrors.password || undefined}
              aria-describedby={registerErrors.password ? 'password-error' : undefined}
              {...registerField('password')}
            />
            <FieldError id="password-error" error={registerErrors.password} />
          </div>

          <div className="form-field">
            <label htmlFor="confirmPassword">Confirm password</label>
            <input
              id="confirmPassword"
              type="password"
              autoComplete="new-password"
              disabled={isSubmitting}
              aria-invalid={!!registerErrors.confirmPassword || undefined}
              aria-describedby={registerErrors.confirmPassword ? 'confirmPassword-error' : undefined}
              {...registerField('confirmPassword')}
            />
            <FieldError id="confirmPassword-error" error={registerErrors.confirmPassword} />
          </div>

          <button
            type="submit"
            data-testid="submit-btn"
            className="btn-primary btn-full"
            aria-busy={submitState.ariaBusy}
            disabled={submitState.ariaBusy}
          >
            {submitState.buttonLabel}
          </button>
        </form>

        <p className="auth-link">
          Already have an account? <Link to="/login">Sign in</Link>
        </p>
      </div>
    </div>
  );
}
